import { ReactElement } from "react";
import { alpha, Box, Container, Grid, Link, Stack, Typography, useTheme } from "@mui/material";
import { composeServerSideProps } from "next-composition";
import { withTranslations } from "../../../server/ssrprops/withTranslations";
import { useTranslation } from "../../../fe-ui/hooks/use-translation";
import { FeatureCard } from "../../../ui/components/FeatureCard/FeatureCard";
import getConfig from "next/config";
import { LogoSvgOnly } from "../../../fe-ui/atoms/Logo/LogoSvgOnly";
import { responsiveFontSizes } from "../../../fe-ui/theme/typography";
import { withSeoSsrProps } from "../../../server/ssrprops/withSeoSsrProps";

const ApplyIndexPage = () => {
  const t = useTranslation();
  const theme = useTheme();
  const { publicRuntimeConfig } = getConfig();
  const appVersion = `, v${publicRuntimeConfig.appVersion}`;

  return (
    <>
      <Box
        sx={{
          // minWidth: "100vw",
          minHeight: "100vh",
          py: 2,
          px: 0,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Stack spacing={2} direction={"column"}>
            <Box sx={{ margin: "0 auto" }}>
              <Link href={"/"} sx={{ display: "flex", justifyContent: "center" }}>
                <LogoSvgOnly width={{ xs: 183, lg: 183 }} height={{ xs: 65, lg: 65 }} isDark />
              </Link>
              <Typography
                sx={{
                  width: "229px",
                  fontWeight: 400,
                  lineHeight: 20 / 14,
                  fontSize: 14,
                  margin: "16px auto 0",
                }}
              >
                {t("apply_index_logo_slogan")}
              </Typography>
              <Typography variant="h1" sx={{ lineHeight: 68 / 64, margin: "40px auto 0" }}>
                {t("apply_index_hero_title")}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  lineHeight: 32 / 24,
                  ...responsiveFontSizes({ sm: 20, md: 22, lg: 24 }),
                  margin: "16px auto 0",
                }}
              >
                {t.rich("apply_index_hero_subtitle", { br: () => <br /> })}
              </Typography>
            </Box>
          </Stack>
        </Container>

        <Container sx={{ maxWidth: 1024, marginTop: "64px" }}>
          <Stack
            sx={{
              padding: "64px",
              background: alpha(theme.palette.primary.main, 0.32),
              borderRadius: "32px",
              textAlign: "center",
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 600,
                lineHeight: 52 / 48,
                fontSize: 36,
                ...responsiveFontSizes({ sm: 40, md: 44, lg: 48 }),
                margin: "0 auto",
              }}
            >
              {t.rich("apply_index_main_container_title", { br: () => <br /> })}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                maxWidth: 760,
                weight: 400,
                lineHeight: 28 / 18,
                fontSize: 18,
                margin: "16px auto 0",
              }}
            >
              {t("apply_index_main_container_body")}
            </Typography>

            <Grid container spacing={3} mt={5}>
              <Grid item md={4} sm={12}>
                <FeatureCard
                  href="/info/apply/for-private-owners"
                  title={t.rich("apply_index_feature_1_title", { br: () => <br /> })}
                  description={t("apply_index_feature_1_description")}
                />
              </Grid>
              <Grid item md={4} sm={12}>
                <FeatureCard
                  href="/info/apply/trust-management"
                  title={t.rich("apply_index_feature_2_title", { br: () => <br /> })}
                  description={t("apply_index_feature_2_description")}
                />
              </Grid>
              <Grid item md={4} sm={12}>
                <FeatureCard
                  href="/info/apply/for-hotels"
                  title={t.rich("apply_index_feature_3_title", { br: () => <br /> })}
                  description={t("apply_index_feature_3_description")}
                />
              </Grid>
            </Grid>
          </Stack>
        </Container>
        <Typography
          variant={"body2"}
          component={"div"}
          sx={{
            mt: 8,
            pb: 3,
            lineWeight: 400,
            lineHeight: 20 / 14,
            fontSize: 14,
            textAlign: "center",
          }}
        >
          {t("Footer_copy")}

          {appVersion}
        </Typography>
      </Box>
    </>
  );
};

ApplyIndexPage.getLayout = (page: ReactElement) => <> {page} </>;

export const getServerSideProps = composeServerSideProps({
  use: [withTranslations, withSeoSsrProps],
});

export default ApplyIndexPage;
